/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {
  useState,
  useMemo,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router-dom';
import errorReporter from '@app/errorReporter';
import { AuthContext, AuthContextType, ListingLabelContext } from 'contexts';
import {
  ROLES,
  ITransferUser,
  TransferState,
  isUserLicensed,
  UserLicenseType,
  IMLSNumbers,
  UserRoles,
  Titles,
} from '@bb/users';
import { Feature } from '@bb/automaton';

import { Page, Button, Text, message, Popconfirm, theme } from 'components';

import {
  buildCssProps,
  buildErrorMessage,
  featureEnabled,
  isAssumeOrgType,
  MakeRequired,
} from 'utils';

import {
  desyncCalendar,
  updateAlmanacAgentUserSubscription,
} from 'api/AlmanacController';
import {
  save as saveUser,
  currentSave as saveCurrentUser,
  requestUserTransfer,
  updateTransferUser,
  saveShowingDefaultSettings,
} from 'api/UserController';
import { isEqual, omit } from 'lodash';
import { addTeamDefaultContact } from 'api/TeamController';
import {
  updatePinForDirectKey,
  updateNotificationSettings,
} from 'api/SupraLockboxController';

import { Pronoun } from '@bb/users/dist/types/enums';
import {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types/SupraSystemSubscription';
import type { IUserRequestUserTransferBody } from '@/app-package';
import { unassume } from '../../../components/AssumeNavbar/AnsweringAssumeNavbar';
import UserHistoryModal, {
  UserHistoryModalType,
  UserTab,
} from '../../ManageUsersPage/UserHistoryModal';

import AddToTeamContactsModal from '../AddToTeamContactsModal';

import FormContext, { initialSettings } from '../FormContext';
import ProfileTab from '../ProfileTab';
import SupraSubscriptionTab from '../SupraSubscriptionTab';
import FrontDeskInstructionsTab from '../FrontDeskTab';
import ListingSettingsTab from '../ListingSettingsTab';
import { Divider } from '../components';
import PageHeader from './PageHeader';
import PageBreadcrumbs from './PageBreadcrumbs';
import PageTabs from './PageTabs';
import PageAlert from './PageAlert';
import { TabKey } from '../types';
import {
  NO_BOARD,
  REQUEST_TRANSFER_TEXT,
  NOT_EDITABLE_PAGE,
  UNCLAIMED_ORG_NO_ACCESS_PAGES,
} from '../constants';
import { cantRequestTransfer, shouldRequestTransfer } from '../util';
import AppointmentDeskPage from '../../AppointmentDeskPage';
import BillingInvoicesPage from '../../BillingInvoicesPage';
import PermissionsTab from '../PermissionsTab';
import AgentUserCalendarContext from '../../ShowingsCalendarPage/AgentUserCalendarContext';
import { SUBSCRIPTION_UPDATED_EVENT } from '../../BillingInvoicesPage/components/BalanceCard';

interface UserViewPageProps {
  showHistoryModal?: boolean;
  fromDeletedTab?: boolean;
}
const UserViewPage = (props: UserViewPageProps) => {
  const { showHistoryModal, fromDeletedTab } = props;

  const history = useHistory();

  const { auth, hasFeature, hasLimitedAccess } = useContext(
    AuthContext,
  ) as MakeRequired<AuthContextType, 'auth'>;
  const { getMemberNumberString } = useContext(ListingLabelContext);

  const {
    isNew,
    isProfile,
    isIncomingTransfer,
    userId,
    doesUserOwnProfile,
    transferId,
    transfer,
    loading,
    activeTab,
    memberNumberStates,
    primaryMemberNumberState,
    primaryNumber,
    saving,
    setSaving,
    memberNumberStateLoading,
    userState,
    setUserState,
    fetchedUserState,
    firstNameValid,
    lastNameValid,
    emailValidity,
    branchValid,
    addressValid,
    memberNumbersAllValid,
    mobileValid,
    allValid,
    setWorking,
    working,
    isAnsweringOrMlsStaff,
    isDeleted,
    isOutgoingTransfer,
    handleInvalidSubmit,
    setActiveTab,
    showingSettings,
    supraSubscription,
    setSupraSubscription,
    showForwardReps,
    setShowForwardReps,
  } = useContext(FormContext);

  const {
    setNotAuthenticated,
    agentUserCalendarSubscriptions,
    calendarProvider,
    existingCalendarProvider,
  } = useContext(AgentUserCalendarContext);

  const {
    avatarFilestackId,
    avatarFullFilestackId,
    firstName,
    lastName,
    role,
    subRole,
    title,
    licenseType,
    memberNumbers,
    mobile,
    officeExtension,
    altPhone,
    altPhoneType,
    email,
    branch,
    branchAccess,
    teamAccess,
    team,
    address,
    password,
    disabled,
    isAgentChatHidden,
    isChatAdmin,
    isChatInactive,
    duplicateNotify,
    expiryReminder,
    feedbackCollected,
    feedbackNotify,
    feedbackSendToSeller,
    frontEndInstructions,
    offerSettings,
    pronoun,
    notes,
    features,
    supraSubscription: userSupraSubscription,
  } = userState;

  const [isUserHistoryModalVisible, setIsUserHistoryModalVisible] =
    useState(false);

  const [teamAdminContactsModalVisible, setTeamAdminContactsModalVisible] =
    useState(false);

  const [isSupraPinError, setIsSupraPinError] = useState(false);

  useEffect(() => {
    if (showHistoryModal) setIsUserHistoryModalVisible(true);
  }, [showHistoryModal]);

  const closeModal = () => {
    setIsUserHistoryModalVisible(false);
  };

  const navigateToManageUsers = useCallback(() => {
    history.push('/users/list');
  }, [history]);

  const navigateToAnsweringManageUsers = useCallback(() => {
    history.push('/answering/manage-staff');
  }, [history]);

  const navigateToTransfersList = useCallback(() => {
    history.push('/users/list?tabKey=transfers');
  }, [history]);

  const [initialSupraSubscriptionType, setInitialSupraSubscriptionType] =
    useState<string | undefined>();

  useEffect(() => {
    // Check if transfer is in completed state and navigate to appropriate page
    if (isIncomingTransfer && transfer) {
      if (transfer.state === TransferState.Active) {
        history.push(`/users/edit/${transfer.transferredUserId}`);
      } else if (transfer.state !== TransferState.Pending) {
        navigateToTransfersList();
      }
    }
  }, [isIncomingTransfer, transfer, history, navigateToTransfersList]);

  useEffect(() => {
    if (fetchedUserState) {
      setInitialSupraSubscriptionType(
        fetchedUserState.supraSubscription?.type || undefined,
      );
    }
  }, [fetchedUserState]);

  const showSupraSubscriptionMessages = useCallback(() => {
    message.success('Supra subscription created');
    message.success('Supra subscription invitation sent');
  }, []);

  const pageTitle = useMemo(() => {
    if (isNew) return 'New User';
    if (fetchedUserState)
      return `${fetchedUserState.firstName} ${fetchedUserState.lastName}`;
    // Placeholder for when user's name hasn't been fetched yet
    return 'Loading User...';
  }, [isNew, fetchedUserState]);

  const breadcrumbRoutes = useMemo(() => {
    const routes = [
      { breadcrumbName: 'Admin', path: '/admin' },
      {
        breadcrumbName: 'Manage Users',
        path: isAnsweringOrMlsStaff
          ? fromDeletedTab
            ? '/answering/manage-deleted-staff'
            : '/answering/manage-staff'
          : '/users/list',
      },
    ];

    if (isIncomingTransfer || isOutgoingTransfer) {
      routes.push({
        breadcrumbName: 'Transfers',
        path: '/users/list?tabKey=transfers',
      });
    }
    routes.push({ breadcrumbName: pageTitle, path: history.location.pathname });
    return routes;
  }, [
    isAnsweringOrMlsStaff,
    isIncomingTransfer,
    isOutgoingTransfer,
    pageTitle,
    history,
    fromDeletedTab,
  ]);

  // Navigate to 2nd last breadcrumb route
  const navigateBack = useCallback(() => {
    if (isProfile) {
      history.push('/');
      return;
    }
    history.push(breadcrumbRoutes[breadcrumbRoutes.length - 2].path);
  }, [isProfile, breadcrumbRoutes, history]);

  const handleCancel = () => {
    if (isAnsweringOrMlsStaff) navigateToAnsweringManageUsers();
    else navigateBack();
  };

  // Handle building a user from the "user" object props passed in from the UI form.
  function buildUser() {
    let user: Omit<ITransferUser, 'org' | 'team' | 'mls_numbers' | 'teams'> & {
      org?: string;
      team?: string;
      locations?: string[];
      teams?: string[];
      mls_numbers?: IMLSNumbers[];
      pronoun?: Pronoun[];
      notes?: string;
      supraSubscription?: {
        type?: SupraSystemSubscriptionType | '';
        billing?: 'billable' | 'non-billable' | '';
        assignLockboxesReleaseShackles?: boolean;
        waiveActivationFee?: boolean;
        waiveActivationFeeReason?: string;
      };
    };

    if (isAnsweringOrMlsStaff) {
      user = {
        avatarFilestackId,
        avatarFullFilestackId,
        firstName,
        lastName,
        phone: mobile,
        email,

        mls_numbers: [],
        location: '0',
        address: '0',
        role: UserRoles['Global Admin'],
        licenseType: UserLicenseType.UNLICENSED,
        frontEndInstructions: { forwardReps: [] },
        feedbackCollected: true,
        feedbackNotify: true,
        duplicateNotify: true,
        expiryReminder: true,
        offerSettings: { agentGroup: 'none', shouldShowOfferCount: true },
        pronoun,
        pending: null,
        team: null,
        notes,
        features,
      };
    } else {
      user = {
        avatarFilestackId,
        avatarFullFilestackId,
        firstName,
        lastName,
        role,
        location: branch,
        address,
        team,
        phone: mobile,
        email,
        disabled,
        isAgentChatHidden,
        isChatInactive,
        licenseType,
        offerSettings,
        pronoun,
        duplicateNotify,
        expiryReminder,
        feedbackCollected,
        feedbackNotify,
        feedbackSendToSeller,
        ...(frontEndInstructions && { frontEndInstructions }),
        notes,
        features,
      };
    }
    if (
      auth.parentOrgType === 'mlsStaff' &&
      hasFeature(Feature.SUPRA_MVP) &&
      featureEnabled('supraAddUserFeature')
    ) {
      user.supraSubscription = {
        type: userSupraSubscription?.type ?? '',
        billing: userSupraSubscription?.billing ?? '',
        assignLockboxesReleaseShackles:
          userSupraSubscription?.assignLockboxesReleaseShackles,
        waiveActivationFee: userSupraSubscription?.waiveActivationFee,
        waiveActivationFeeReason:
          userSupraSubscription?.waiveActivationFeeReason,
      };
      if (subRole) {
        user.subRole = subRole;
      }
    }

    if (password) user.password = password;
    user.officeExtension = officeExtension || null;
    user.altPhone = altPhone || null;
    user.altPhoneType = altPhone ? altPhoneType : null;

    if (!isAnsweringOrMlsStaff) {
      if (
        [
          UserLicenseType.LICENSED,
          UserLicenseType.UNLICENSED_WITH_MEMBER_NUMBER,
        ].includes(licenseType!)
      ) {
        if (isUserLicensed({ licenseType }) && title)
          user.title = title as Titles;
        // Record any number listed as 'NO_BOARD' as a license number
        const licenseNumber = memberNumbers.find(
          item => item.board === NO_BOARD,
        );
        const mapMlsNumber = (
          item: Required<(typeof memberNumbers)[number]>,
        ) => {
          const result: IMLSNumbers = {
            board: item.board,
            boardId: item.boardId,
            number: item.number,
            isPrimary: item.isPrimary,
          };
          if (item.board !== NO_BOARD) {
            // Using state.mlsUniqueId instead of item.mlsUniqueId since the latter could be stale
            // but the former should always have the correct mlsUniqueId
            const key = getMemberNumberString(item.board, item.number);
            const state = memberNumberStates[key];
            if (state?.multiMatch !== true && state?.mlsUniqueId) {
              result.mlsUniqueId = state.mlsUniqueId;
            }
          }
          return result;
        };

        if (licenseNumber) {
          user.licenseNumber = licenseNumber.number;
          user.mls_numbers = memberNumbers
            .map(mapMlsNumber)
            .filter(item => item.board !== NO_BOARD);
        } else {
          user.mls_numbers = memberNumbers.map(mapMlsNumber);
        }
      }
      if (role === UserRoles['Branch Admin']) user.locations = branchAccess;
      user.teams = teamAccess || [];
      if (ROLES[role] >= ROLES.BRANCH_ADMIN) {
        user.isChatAdmin = isChatAdmin;
      }
      if (isIncomingTransfer) user.org = auth.org.id;
    }
    return user;
  }

  const requestTransfer = React.useCallback(async () => {
    if (
      !primaryMemberNumberState ||
      primaryMemberNumberState.multiMatch === true
    )
      return;

    try {
      setWorking(true);
      const userIdToSend = primaryMemberNumberState.userId!;

      const transferUser = buildUser();

      await requestUserTransfer({
        userId: userIdToSend,
        ...(transferUser as Omit<IUserRequestUserTransferBody, 'userId'>),
      });
      message.success('Transfer requested');
      navigateToTransfersList();
    } catch (err) {
      errorReporter.error(err);
      message.error(
        buildErrorMessage({ err, prefixText: 'Failed to request transfer' }),
      );
    } finally {
      setWorking(false);
    }
  }, [
    navigateToTransfersList,
    primaryMemberNumberState,
    setWorking,
    userState,
    memberNumberStates,
    auth,
  ]);

  const handleAddTAContact = async (id: string, selectedTeams?: string[]) => {
    try {
      await addTeamDefaultContact(id, selectedTeams);
      message.success('Successfully updated all team listings');
    } catch (err) {
      errorReporter.error(err);
      message.error(
        buildErrorMessage({
          err,
          prefixText: 'Failed to add Team Admin as contact to team listings',
        }),
      );
    } finally {
      navigateToManageUsers();
    }
  };

  // Handle saving a NEW user being created to the database.
  const handleSaveUser = useCallback(
    async user => {
      // Send off the new user to be saved.
      const createdUser = await saveUser(userId, user);

      if (!isNew || isAssumeOrgType(auth.org.type)) {
        message.success(`${firstName} ${lastName} updated successfully`);
        setSaving(false);
      } else if (
        // APP-4626/AUT-750: Disable joinBrokerageEmail for pre-launch orgs -->
        hasFeature('PRE_LAUNCH') ||
        // <-- APP-4626/AUT-750
        auth.org.settings?.disableJoinEmail
      ) {
        message.success(`${firstName} ${lastName} created successfully`);
      } else {
        message.success(
          <Text>
            Invitation sent to <Text strong>{user.email}</Text>
          </Text>,
        );
      }
      return createdUser?.id || userId;
    },
    [
      userId,
      isNew,
      auth.org.type,
      hasFeature,
      auth.org.settings?.disableJoinEmail,
      firstName,
      lastName,
      setSaving,
    ],
  );

  const updateAlmanacSettings = useCallback(async () => {
    const desync =
      calendarProvider === 'none' ||
      calendarProvider !== existingCalendarProvider;

    const subIsToggledOn =
      agentUserCalendarSubscriptions?.buyerShowingCalendarSubscription ||
      agentUserCalendarSubscriptions?.listingShowingCalendarSubscription;

    if (desync) {
      setNotAuthenticated();
      await desyncCalendar();
    }

    if (
      calendarProvider !== 'none' &&
      calendarProvider !== existingCalendarProvider &&
      subIsToggledOn
    ) {
      // Changing to new calendar provider, redirect to Calendar sync OAuth
      let redirectUrl = `${window.location.href}?activeTab=LISTING_SETTINGS`;
      if (agentUserCalendarSubscriptions?.buyerShowingCalendarSubscription) {
        redirectUrl += '&buyerShowingCalendarSubscription=true';
      }
      if (agentUserCalendarSubscriptions?.listingShowingCalendarSubscription) {
        redirectUrl += '&listingShowingCalendarSubscription=true';
      }

      const oauthUrl = `/almanac/connect?service=${calendarProvider}&redirectUrl=${encodeURIComponent(
        redirectUrl,
      )}`;

      window.location.href = oauthUrl;
    } else if (!desync) {
      // Not changing providers and not desyncing, just save the subscription toggles
      await updateAlmanacAgentUserSubscription(agentUserCalendarSubscriptions);
    }
  }, [
    agentUserCalendarSubscriptions,
    calendarProvider,
    existingCalendarProvider,
    setNotAuthenticated,
  ]);

  const handleSaveSupraDetails = useCallback(async () => {
    if (
      activeTab !== 'SUPRA_SUBSCRIPTION' ||
      !(hasFeature(Feature.SUPRA_MVP) && supraSubscription)
    ) {
      return;
    }

    setIsSupraPinError(false);

    if (
      supraSubscription.currentPin &&
      supraSubscription.newPin &&
      userState.id &&
      userState.org
    ) {
      const pinUpdateRes = await updatePinForDirectKey({
        currentPin: supraSubscription.currentPin,
        newPin: supraSubscription.newPin,
        userId: userState.id,
        orgId:
          typeof userState.org === 'string' ? userState.org : userState.org.id,
      });
      if (pinUpdateRes === 'INCORRECT_PIN') {
        message.error('The current PIN is incorrect');
        setIsSupraPinError(true);
        setSupraSubscription(subscription => ({
          ...subscription,
          dirty: false,
        }));
        return;
      }
    }

    await updateNotificationSettings({
      showingStarted: supraSubscription.showingStarted,
      lockboxAssignmentReminder: supraSubscription.lockboxAssignmentReminder,
      userId: userId!,
    });

    setSupraSubscription(subscription => ({
      ...subscription,
      currentPin: '',
      newPin: '',
      confirmingPin: '',
      dirty: false,
    }));

    setSaving(false);
  }, [
    activeTab,
    hasFeature,
    setSaving,
    setSupraSubscription,
    supraSubscription,
    userId,
    userState.id,
    userState.org,
  ]);

  // Handle saving updates to an EXISTING user to the database.
  const handleSave = useCallback(
    async () => {
      if (!allValid) {
        handleInvalidSubmit();
        return;
      }

      setSaving(true);
      const user = buildUser();

      // Toggle off if forward rep contacts are empty
      if (!user.frontEndInstructions?.forwardReps?.length && showForwardReps) {
        setShowForwardReps(false);
      }

      // Check if this is a new Supra subscription
      const isCreatingSupraSubscription =
        (!initialSupraSubscriptionType && userState.supraSubscription?.type) ||
        (fetchedUserState?.supraSubscription?.status ===
          SupraSystemSubscriptionStatus.unsubscribed &&
          userState.supraSubscription?.type !==
            fetchedUserState?.supraSubscription?.type);

      try {
        if (isProfile) {
          // Send off the current user to be saved.
          const savedUser = await saveCurrentUser(user);

          if (
            !hasLimitedAccess &&
            !isEqual(omit(showingSettings, 'userId'), initialSettings)
          ) {
            await saveShowingDefaultSettings(showingSettings);
          }

          // Updating Almanac settings may involve redirecting to OAuth
          // This should occur after all other API calls
          if (agentUserCalendarSubscriptions && !hasLimitedAccess) {
            await updateAlmanacSettings();
          }

          await handleSaveSupraDetails();

          // Send a signal that the subscription may have been updated so other pages can dynamically re-render content
          document.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT));

          setSaving(false);
          setUserState(cur => ({ ...cur, ...savedUser }));

          if (isCreatingSupraSubscription) {
            showSupraSubscriptionMessages();
          }
          message.success('Your profile updated successfully');
        } else if (isIncomingTransfer) {
          await updateTransferUser({
            incomingTransferId: transferId!,
            // @ts-expect-error: mismatch JsonObject type
            updatedTransferUser: user,
          });

          if (isCreatingSupraSubscription) {
            showSupraSubscriptionMessages();
          }
          message.success(`${firstName} ${lastName} updated successfully`);
          navigateToTransfersList();
        } else if (role === 'TEAM_ADMIN') {
          setTeamAdminContactsModalVisible(true);
        } else {
          // If it's not an existing user, pass this off to a different handler to deal with saving a brand new user.
          const savedUserId = await handleSaveUser(user);

          if (isCreatingSupraSubscription) {
            showSupraSubscriptionMessages();
          }

          if (
            agentUserCalendarSubscriptions &&
            !hasLimitedAccess &&
            doesUserOwnProfile
          ) {
            await updateAlmanacSettings();
          }
          await handleSaveSupraDetails();

          // Send a signal that the subscription may have been updated so other pages can dynamically re-render content
          document.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT));

          if (
            auth.parentOrgType === 'mlsStaff' &&
            featureEnabled('supraAddUserFeature')
          ) {
            // Delay the unassume redirect to let user see the message
            setTimeout(() => {
              unassume(auth, {
                redirect: encodeURIComponent(
                  `/#/mls/users/0?userRedirectId="${savedUserId}"`,
                ),
              });
            }, 2000);
          } else if (isAnsweringOrMlsStaff) {
            navigateToAnsweringManageUsers();
          } else if (!isSupraPinError) {
            navigateToManageUsers();
          }
        }
      } catch (err) {
        errorReporter.error(err);
        message.error(
          buildErrorMessage({ err, prefixText: 'Failed to save user' }),
        );
        setSaving(false);
      }
    },
    // Intentionally omitting buildUser() from the dependency array so that
    // it re-creates after every render. See https://github.com/BrokerBay/app/pull/14756/
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allValid,
      setSaving,
      initialSupraSubscriptionType,
      userState.supraSubscription?.type,
      fetchedUserState?.supraSubscription?.status,
      fetchedUserState?.supraSubscription?.type,
      handleInvalidSubmit,
      isProfile,
      isIncomingTransfer,
      role,
      hasLimitedAccess,
      showingSettings,
      agentUserCalendarSubscriptions,
      handleSaveSupraDetails,
      setUserState,
      updateAlmanacSettings,
      showSupraSubscriptionMessages,
      transferId,
      firstName,
      lastName,
      navigateToTransfersList,
      handleSaveUser,
      doesUserOwnProfile,
      auth,
      isAnsweringOrMlsStaff,
      isSupraPinError,
      navigateToAnsweringManageUsers,
      navigateToManageUsers,
    ],
  );

  const bodyContentMap: Record<TabKey, JSX.Element> = {
    PROFILE: <ProfileTab requestTransfer={requestTransfer} />,
    SUPRA_SUBSCRIPTION: <SupraSubscriptionTab />,
    FRONT_DESK_INSTRUCTIONS: <FrontDeskInstructionsTab />,
    LISTING_SETTINGS: <ListingSettingsTab />,
    LIVE_CONCIERGE_SERVICE: (
      <AppointmentDeskPage
        subscriptionType="user"
        userId={userId}
        billingRedirection={() => setActiveTab('BILLING')}
        userMlsNumbers={fetchedUserState?.memberNumbers}
      />
    ),
    NOTIFICATION_PREFERENCES: <></>,
    BILLING: (
      <BillingInvoicesPage
        userId={userId}
        userMlsNumbers={fetchedUserState?.memberNumbers}
      />
    ),
    PERMISSIONS: <PermissionsTab />,
  };

  const getSaveButtonText = useCallback(() => {
    if (
      auth.org.features?.includes('SUPRA_MVP') &&
      featureEnabled('supraAddUserFeature') &&
      auth.parentOrgType === 'mlsStaff'
    ) {
      if (isNew) {
        return 'Create and Subscribe';
      }

      if (
        !userState.supraSubscription ||
        (userState.supraSubscription.status ===
          SupraSystemSubscriptionStatus.unsubscribed &&
          userState.supraSubscription.type !==
            fetchedUserState?.supraSubscription?.type)
      ) {
        return 'Edit and Subscribe';
      }
    }

    return 'Save';
  }, [
    auth.org.features,
    auth.parentOrgType,
    fetchedUserState?.supraSubscription?.type,
    isNew,
    userState.supraSubscription,
  ]);

  const saveButton = React.useMemo(() => {
    if (
      shouldRequestTransfer({
        primaryMemberNumberState,
        primaryNumber,
        firstNameValid,
        lastNameValid,
        isNew,
        licenseType: licenseType!,
      })
    ) {
      const linkDisabled = cantRequestTransfer({
        branchValid,
        addressValid,
        memberNumbersAllValid,
        emailValid: emailValidity.isValid,
        mobileValid,
        memberNumberStateLoading,
      });

      return (
        <Popconfirm
          title={REQUEST_TRANSFER_TEXT}
          okText="Yes"
          cancelText="No"
          onConfirm={requestTransfer}
          width={336}
          disabled={linkDisabled}
          placement="topRight"
        >
          <BottomButton
            disabled={saving || working || linkDisabled}
            type="primary"
          >
            Request Transfer
          </BottomButton>
        </Popconfirm>
      );
    }

    return (
      <BottomButton
        data-cy="user-button-save"
        disabled={saving || working || memberNumberStateLoading}
        onClick={handleSave}
        type="primary"
      >
        {getSaveButtonText()}
      </BottomButton>
    );
  }, [
    primaryMemberNumberState,
    primaryNumber,
    firstNameValid,
    lastNameValid,
    isNew,
    licenseType,
    saving,
    working,
    memberNumberStateLoading,
    handleSave,
    getSaveButtonText,
    branchValid,
    addressValid,
    memberNumbersAllValid,
    emailValidity.isValid,
    mobileValid,
    requestTransfer,
  ]);

  const onNotEditablePage = useMemo(
    () => NOT_EDITABLE_PAGE.includes(activeTab),
    [activeTab],
  );

  const shouldBlurTab =
    UNCLAIMED_ORG_NO_ACCESS_PAGES.includes(activeTab) && hasLimitedAccess;

  return (
    <Page
      type="hollow"
      overTitle={<PageBreadcrumbs breadcrumbRoutes={breadcrumbRoutes} />}
      headerExtra={
        <PageHeader
          openHistoryModal={() => setIsUserHistoryModalVisible(true)}
          navigateBack={navigateBack}
          pageTitle={pageTitle}
        />
      }
      subHeader={<PageTabs />}
      headerVerticalAlign="baseline"
      headerDirection="column"
      fullTitleWidth
      bodyCss={css`
        padding: 16px;
      `}
    >
      {onNotEditablePage ? (
        bodyContentMap[activeTab]
      ) : (
        <>
          <PageAlert
            handleSave={handleSave}
            navigateBack={navigateBack}
            navigateToTransfersList={navigateToTransfersList}
            requestTransfer={requestTransfer}
          />

          <BodyPane $blur={shouldBlurTab}>
            <BodyContainer>
              {bodyContentMap[activeTab]}

              {isDeleted && (
                <>
                  <Divider />
                  <BottomButton onClick={navigateBack}>Back</BottomButton>
                </>
              )}

              {!loading && !isDeleted && (
                <>
                  <Divider />
                  {saveButton}
                  {isNew &&
                  auth.org.features?.includes('SUPRA_MVP') &&
                  featureEnabled('supraAddUserFeature') &&
                  auth.parentOrgType === 'mlsStaff' ? null : (
                    <BottomButton
                      data-cy="user-button-cancel"
                      onClick={handleCancel}
                    >
                      Cancel
                    </BottomButton>
                  )}
                </>
              )}
            </BodyContainer>
          </BodyPane>
        </>
      )}

      <UserHistoryModal
        isModalVisible={isUserHistoryModalVisible}
        userId={userId!}
        handleCancel={closeModal}
        tabsToDisplay={isProfile ? [UserTab.DETAILS] : undefined}
        type={UserHistoryModalType.USER}
        transfer={transfer}
      />
      <AddToTeamContactsModal
        visible={teamAdminContactsModalVisible}
        onCancel={() => {
          setSaving(false);
          setTeamAdminContactsModalVisible(false);
        }}
        onDontAdd={async () => {
          try {
            await handleSaveSupraDetails();
            await handleSaveUser(buildUser());
            navigateToManageUsers();
          } catch (err) {
            errorReporter.error(err);
            message.error(
              buildErrorMessage({ err, prefixText: 'Failed to save user' }),
            );
            setSaving(false);
            setTeamAdminContactsModalVisible(false);
          }
        }}
        onAdd={async (selectedTeams?: string[]) => {
          try {
            await handleSaveSupraDetails();
            const id = await handleSaveUser(buildUser());
            await handleAddTAContact(id, selectedTeams);
          } catch (err) {
            errorReporter.error(err);
            message.error(
              buildErrorMessage({ err, prefixText: 'Failed to save user' }),
            );
            setSaving(false);
            setTeamAdminContactsModalVisible(false);
          }
        }}
        teamAccess={teamAccess}
      />
    </Page>
  );
};

export default UserViewPage;

const BodyPane = styled.div<{ $blur?: boolean }>`
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  ${({ $blur }) =>
    buildCssProps([
      ['filter', $blur && `blur(${theme.pageBlurRadius})`],
      ['pointer-events', $blur && 'none'],
    ])}
`;

const BodyContainer = styled.div`
  padding: 24px;
  width: min(640px, 100%);
`;

const BottomButton = styled(Button)`
  &:not(:last-child) {
    margin-right: 16px;
  }
`;
